import cooperantOrdersQueries from '@/api/modules/cooperantOrdersQueries'
import cooperantDeliveryStatusesQueries from '@/api/modules/cooperantDeliveryStatusesQueries'
import cooperantOrderStatusesQueries from '@/api/modules/cooperantOrderStatusesQueries'
import cooperantOrdersLookupQueries from '@/api/modules/cooperantOrdersLookupQueries'
import { t } from '@/i18n'
import { notificationService } from '@/services/notificationService'
import { handleApiError } from '@/utils/apiHelpers'

class CooperantOrdersService {
    async fetchOrders(queryString = '') {
        try {
            return await cooperantOrdersQueries.fetchOrders(queryString)
        } catch (error) {
            handleApiError(error, t('orders.cooperant.service.fetchOrdersError'))
            throw error
        }
    }

    async fetchOrder(orderId) {
        try {
            return await cooperantOrdersQueries.fetchOrder(orderId)
        } catch (error) {
            handleApiError(error, t('orders.cooperant.service.fetchOrderError'))
            throw error
        }
    }

    async fetchByOrderNumbers(orderNumbers) {
        try {
            return await cooperantOrdersLookupQueries.fetchByOrderNumbers(orderNumbers)
        } catch (error) {
            handleApiError(error, t('orders.cooperant.service.fetchOrdersByNumbersError'))
            throw error
        }
    }

    async fetchCooperantDeliveryStatusNames() {
        try {
            return await cooperantDeliveryStatusesQueries.fetchAll()
        } catch (error) {
            handleApiError(error, t('orders.cooperant.service.fetchDeliveryStatusesError'))
            throw error
        }
    }

    async fetchOrderStatuses() {
        try {
            return await cooperantOrderStatusesQueries.fetchAll()
        } catch (error) {
            handleApiError(error, t('orders.cooperant.service.fetchOrderStatusesError'))
            throw error
        }
    }

    async addComment(commentData) {
        try {
            const response = await cooperantOrdersQueries.addComment(commentData)
            notificationService.success(t('orders.cooperant.service.addCommentSuccess'))
            return response
        } catch (error) {
            handleApiError(error, t('orders.cooperant.service.addCommentError'))
            throw error
        }
    }

    async generateCooperantOrdersListReport(queryString = '', userEmail) {
        try {
            const response = await cooperantOrdersQueries.generateCooperantOrdersListReport(queryString)
            notificationService.success(t('orders.cooperant.service.generateReportSuccess', { email: userEmail }))
            return response
        } catch (error) {
            handleApiError(error, t('orders.cooperant.service.generateReportError'))
            throw error
        }
    }

    async printCooperantLabels(payload) {
        try {
            const response = await cooperantOrdersQueries.printCooperantLabels(payload)
            notificationService.success(t('orders.cooperant.service.printLabelsSuccess'))
            return response
        } catch (error) {
            handleApiError(error, t('orders.cooperant.service.printLabelsError'))
            throw error
        }
    }

    async provideCooperationShipmentDeliveryNumber(payload) {
        try {
            const response = await cooperantOrdersQueries.provideCooperationShipmentDeliveryNumber(payload)
            notificationService.success(t('orders.cooperant.service.provideDeliveryNumberSuccess'))
            return response
        } catch (error) {
            handleApiError(error, t('orders.cooperant.service.provideDeliveryNumberError'))
            throw error
        }
    }

    async updateOrder(orderData) {
        try {
            return await cooperantOrdersQueries.updateOrder(orderData)
        } catch (error) {
            handleApiError(error, t('orders.cooperant.service.updateOrderError'))
            throw error
        }
    }

    async updateDelivery(deliveryId, payload) {
        try {
            return await cooperantOrdersQueries.updateDelivery(deliveryId, payload)
        } catch (error) {
            handleApiError(error, t('orders.cooperant.service.updateDeliveryError'))
            throw error
        }
    }

    async deleteOrder(orderId) {
        try {
            const response = await cooperantOrdersQueries.deleteOrder(orderId)
            notificationService.success(t('orders.cooperant.service.deleteOrderSuccess'))
            return response
        } catch (error) {
            handleApiError(error, t('orders.cooperant.service.deleteOrderError'))
            throw error
        }
    }

    async fetchToolboxTemplateContent(toolboxURI, templateId) {
        try {
            return await cooperantOrdersQueries.fetchToolboxTemplateContent(toolboxURI, templateId)
        } catch (error) {
            handleApiError(error, t('orders.cooperant.service.toolboxError'))
            throw error
        }
    }
}

export const useCooperantOrdersService = () => new CooperantOrdersService()
